import { useState, useEffect } from "react";
import { useUser, useFunctions } from "reactfire";
import { httpsCallable } from "firebase/functions";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Textarea,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Tooltip,
    Spacer
} from "@nextui-org/react";
import { MdContentCopy, MdDelete, MdWarning } from "react-icons/md";

interface ApiKey {
    keyId: string;
    name: string;
    description: string;
    createdAt: any;
    lastUsedAt: any;
    active: boolean;
}

export const Settings = () => {
    const user = useUser();
    const functions = useFunctions();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [newKeyDescription, setNewKeyDescription] = useState("");
    const [createdKey, setCreatedKey] = useState<{ apiKey: string; keyId: string } | null>(null);
    const { isOpen: isKeyModalOpen, onOpen: onKeyModalOpen, onClose: onKeyModalClose } = useDisclosure();
    const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

    useEffect(() => {
        loadApiKeys();
    }, [user]);

    const loadApiKeys = async () => {
        if (!user.data) return;

        try {
            setLoading(true);
            const fnName = process.env.NODE_ENV === 'development' ? 'api//keys' : 'api/keys';
            const callableFunction = httpsCallable(functions, fnName);
            const response = await callableFunction({});

            // Firebase callable functions wrap the response in data
            const responseData = response.data as any;
            console.log('API Keys response:', responseData); // Debug log

            // Handle different response structures
            let keys: ApiKey[] = [];
            if (responseData) {
                if (responseData.data && Array.isArray(responseData.data)) {
                    keys = responseData.data;
                } else if (Array.isArray(responseData)) {
                    keys = responseData;
                }
            }

            console.log('Parsed keys:', keys); // Debug log
            setApiKeys(keys);
        } catch (error: any) {
            console.error('Error loading API keys:', error);
            console.error('Error details:', error.message, error.code, error.details);
            // Set empty array on error to avoid showing stale data
            setApiKeys([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!newKeyName.trim()) return;

        try {
            setCreating(true);
            const fnName = process.env.NODE_ENV === 'development' ? 'api//keys/create' : 'api/keys/create';
            const callableFunction = httpsCallable(functions, fnName);
            const response = await callableFunction({
                name: newKeyName,
                description: newKeyDescription
            });

            // Firebase callable functions wrap the response in data
            const responseData = response.data as any;
            console.log('Create key response:', responseData); // Debug log
            const keyData = responseData.data || responseData;
            console.log('Parsed key data:', keyData); // Debug log

            setCreatedKey({
                apiKey: keyData.apiKey,
                keyId: keyData.keyId
            });
            setNewKeyName("");
            setNewKeyDescription("");
            onClose();
            onKeyModalOpen();
            // Reload API keys after creating
            await loadApiKeys();
        } catch (error: any) {
            console.error('Error creating API key:', error);
            alert(error.message || 'Failed to create API key. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const handleDeactivateKey = async () => {
        if (!deleteKeyId) return;

        try {
            setLoading(true);
            const fnName = process.env.NODE_ENV === 'development'
                ? `api//keys/${deleteKeyId}/deactivate`
                : `api/keys/${deleteKeyId}/deactivate`;
            const callableFunction = httpsCallable(functions, fnName);
            await callableFunction({});

            await loadApiKeys();
            onDeleteModalClose();
            setDeleteKeyId(null);
        } catch (error) {
            console.error('Error deactivating API key:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'Never';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    if (!user.data) {
        return <div>Please sign in to access settings.</div>;
    }

    return (
        <div className="flex w-full min-h-[65vh] flex-col gap-4">
            <div className="text-2xl font-bold">Settings</div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center w-full">
                        <h3 className="text-xl font-semibold">API Keys</h3>
                        <Button color="primary" onPress={onOpen}>
                            Create New API Key
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {loading && apiKeys.length === 0 ? (
                        <div>Loading...</div>
                    ) : apiKeys.length === 0 ? (
                        <div className="text-gray-500">No API keys created yet. Create one to get started.</div>
                    ) : (
                        <Table aria-label="API keys table">
                            <TableHeader>
                                <TableColumn>NAME</TableColumn>
                                <TableColumn>DESCRIPTION</TableColumn>
                                <TableColumn>STATUS</TableColumn>
                                <TableColumn>CREATED</TableColumn>
                                <TableColumn>LAST USED</TableColumn>
                                <TableColumn>ACTIONS</TableColumn>
                            </TableHeader>
                            <TableBody>
                                {apiKeys.map((key) => (
                                    <TableRow key={key.keyId}>
                                        <TableCell>{key.name}</TableCell>
                                        <TableCell>{key.description || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                color={key.active ? "success" : "default"}
                                                variant="flat"
                                                size="sm"
                                            >
                                                {key.active ? "Active" : "Inactive"}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>{formatDate(key.createdAt)}</TableCell>
                                        <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                                        <TableCell>
                                            {key.active ? (
                                                <Button
                                                    isIconOnly
                                                    color="danger"
                                                    variant="light"
                                                    onPress={() => {
                                                        setDeleteKeyId(key.keyId);
                                                        onDeleteModalOpen();
                                                    }}
                                                    title="Deactivate key"
                                                >
                                                    <MdDelete />
                                                </Button>
                                            ) : (
                                                <span className="text-gray-400 text-sm">Deactivated</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Create API Key Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalContent>
                    <ModalHeader>Create New API Key</ModalHeader>
                    <ModalBody>
                        <Input
                            label="Name"
                            placeholder="Enter API key name"
                            value={newKeyName}
                            onValueChange={setNewKeyName}
                            isRequired
                        />
                        <Spacer y={2} />
                        <Textarea
                            label="Description"
                            placeholder="Enter API key description (optional)"
                            value={newKeyDescription}
                            onValueChange={setNewKeyDescription}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" variant="light" onPress={onClose}>
                            Cancel
                        </Button>
                        <Button color="primary" onPress={handleCreateKey} isLoading={creating}>
                            Create
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Created Key Modal */}
            <Modal isOpen={isKeyModalOpen} onClose={onKeyModalClose} size="2xl">
                <ModalContent>
                    <ModalHeader>API Key Created</ModalHeader>
                    <ModalBody>
                        <div className="flex flex-col gap-4">
                            <Chip color="warning" variant="flat" startContent={<MdWarning />}>
                                This is the only time you'll see this API key. Copy it now!
                            </Chip>
                            <div>
                                <label className="text-sm font-medium">Your API Key:</label>
                                <div className="flex gap-2 mt-2">
                                    <Input
                                        value={createdKey?.apiKey || ''}
                                        readOnly
                                        className="font-mono"
                                    />
                                    <Tooltip content="Copy to clipboard">
                                        <Button
                                            isIconOnly
                                            variant="flat"
                                            onPress={() => copyToClipboard(createdKey?.apiKey || '')}
                                        >
                                            <MdContentCopy />
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onPress={() => {
                            onKeyModalClose();
                            loadApiKeys(); // Refresh list after closing modal
                        }}>
                            I've Saved My Key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Deactivate Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
                <ModalContent>
                    <ModalHeader>Deactivate API Key</ModalHeader>
                    <ModalBody>
                        <p>Are you sure you want to deactivate this API key? The key will no longer work for API requests, but you can view its usage history. </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" variant="light" onPress={onDeleteModalClose}>
                            Cancel
                        </Button>
                        <Button color="danger" onPress={handleDeactivateKey} isLoading={loading}>
                            Deactivate
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

